import { fade, hex, mixColor, withAlpha, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { BaseTheme, derivePalette, makeMetrics } from "./BaseTheme.js";
import { drawCheckmark } from "./glyphs.js";
import type {
  CaptionButton,
  ProgressVisual,
  ScrollBarParts,
  SpinnerVisual,
  TabVisualState,
  VisualState,
  WindowVisualState,
} from "./Theme.js";

/**
 * "Metro" — the flat, typographic look: no corner is rounded, no surface is
 * shaded, and nothing carries a bevel or a shadow anywhere.
 *
 * What is left to work with is color, alignment and type, so the theme leans
 * on all three. The window has almost no chrome — the caption is the same
 * color as the body, set in a light weight two sizes up, and what marks the
 * active window is a 2px accent frame around it. Controls carry a flat fill
 * and no border until you point at them, at which point they gain a hard 2px
 * outline — that outline is the whole hover state, and it is what stands in
 * for the bevel the flat themes gave up.
 *
 * WinRT inverted a pressed button outright — the fill went to ink and the
 * caption to the ground. That is not safe here. `Toolbar` draws its labels in
 * `palette.text` rather than asking `buttonTextColor`, and it paints a checked
 * toggle as a pressed button, so an inverting theme puts black text on a black
 * face. Pressed is a firm step of the face towards the ink instead: it reads
 * the same, and it stays legible whichever ink the caller picks.
 *
 * Three motifs are worth keeping if you build on this: tabs are a pivot — a
 * row of words with an accent bar under the live one, never a box — the busy
 * marks are traveling dots rather than a sliding block or a chasing ring, and
 * focus is a fine dotted rectangle.
 */

export type MetroVariant = "light" | "dark";

/** The one blue. Dark lifts it a shade to stay legible against near-black. */
const ACCENT_LIGHT = hex("#0072c6");
const ACCENT_DARK = hex("#1c86dd");

/** Caption close: the one hover in the theme that is not the accent. */
const CLOSE_RED = hex("#e81123");
const CLOSE_RED_PRESSED = hex("#c50f1f");

const METRO_FONT = "'Segoe UI', 'Segoe WP', 'Helvetica Neue', Arial, sans-serif";
/** The light-weight face is the most recognizable single part of the look. */
const METRO_CAPTION_FONT = "'Segoe UI Light', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
const METRO_MONO = "Consolas, 'Cascadia Mono', 'Courier New', monospace";

/** Bunched at both ends, quick through the middle: the traveling-dot curve. */
function easeInOut(t: number): number {
  return 0.5 - Math.cos(Math.PI * Math.min(1, Math.max(0, t))) / 2;
}

export class MetroTheme extends BaseTheme {
  readonly variant: MetroVariant;

  constructor(variant: MetroVariant = "light") {
    const dark = variant === "dark";
    const accent = dark ? ACCENT_DARK : ACCENT_LIGHT;
    super({
      name: `metro-${variant}`,
      isDark: dark,
      metrics: makeMetrics({
        // A tall caption for the light-weight title, and controls sized for a
        // finger rather than a mouse — both are Metro to the bone.
        titleBarHeight: 38,
        windowBorder: 2,
        windowCornerRadius: 0,
        resizeGripSize: 6,
        captionButtonWidth: 45,

        controlCornerRadius: 0,
        controlHeight: 32,
        buttonPadding: insets(20, 7),
        buttonMinWidth: 100,
        focusRingWidth: 1,
        focusRingInset: -3,

        textBoxPadding: insets(10, 6),

        checkBoxSize: 20,
        radioSize: 20,
        checkLabelGap: 10,

        splitterSize: 6,
        spinnerSize: 30,

        scrollBarSize: 12,
        scrollBarMinThumb: 28,
        scrollBarArrows: false,

        // Rows run edge to edge; the well has no gutter to inset them into.
        listItemHeight: 30,
        listPadding: insets(0),
        headerHeight: 34,
        columnPadding: 12,

        dialogPadding: 20,
        dialogButtonGap: 10,

        font: { family: METRO_FONT, size: 13 },
        captionFont: { family: METRO_CAPTION_FONT, size: 16, weight: 300 },
        monoFont: { family: METRO_MONO, size: 12.5 },
      }),
      palette: derivePalette(
        dark
          ? {
              desktop: hex("#0d0d0d"),
              surface: hex("#1a1a1a"),
              // A real step off the surface, because there is no border to
              // find a button's edge by until you hover it.
              raised: hex("#333333"),
              field: hex("#000000"),
              ink: hex("#ffffff"),
              accent,
              accentText: hex("#ffffff"),
              border: hex("#4d4d4d"),
              // Chromeless: the caption is the window body, not a bar on it.
              titleBar: hex("#1a1a1a"),
              titleText: hex("#ffffff"),
              isDark: true,
              overrides: {
                // The active frame is the accent, and that is the whole of the
                // window chrome — there is nothing else to look at.
                windowBorderActive: accent,
                windowBorderInactive: hex("#3d3d3d"),
                titleTextInactive: hex("#8a8a8a"),
                controlBgHover: hex("#404040"),
                controlBgPressed: hex("#5c5c5c"),
                controlBgDisabled: hex("#262626"),
                controlBorderHover: hex("#ffffff"),
                textSubtle: hex("#a6a6a6"),
                textDisabled: hex("#5c5c5c"),
                fieldBgDisabled: hex("#141414"),
                selectionInactiveBg: hex("#3a3a3a"),
                textSelectionBg: fade(accent, 0.5),
                scrollTrack: hex("#262626"),
                scrollThumb: hex("#767676"),
                scrollThumbHover: hex("#9e9e9e"),
                scrollThumbPressed: hex("#ffffff"),
                headerBg: hex("#1a1a1a"),
                headerBgHover: hex("#262626"),
                headerText: hex("#a6a6a6"),
                gridLine: hex("#333333"),
                focusRing: hex("#ffffff"),
                // Flat means flat: nothing in here casts.
                shadow: withAlpha(hex("#000000"), 0),
              },
            }
          : {
              // A flat mid-tone ground, so white windows sit on it as blocks
              // rather than dissolving into it.
              desktop: hex("#647687"),
              surface: hex("#ffffff"),
              raised: hex("#cccccc"),
              field: hex("#ffffff"),
              ink: hex("#000000"),
              accent,
              accentText: hex("#ffffff"),
              border: hex("#b3b3b3"),
              titleBar: hex("#ffffff"),
              titleText: hex("#000000"),
              isDark: false,
              overrides: {
                windowBorderActive: accent,
                windowBorderInactive: hex("#c8c8c8"),
                titleTextInactive: hex("#9a9a9a"),
                controlBgHover: hex("#dedede"),
                controlBgPressed: hex("#a3a3a3"),
                controlBgDisabled: hex("#e6e6e6"),
                controlBorderHover: hex("#000000"),
                textSubtle: hex("#6b6b6b"),
                textDisabled: hex("#a6a6a6"),
                fieldBgDisabled: hex("#f2f2f2"),
                selectionInactiveBg: hex("#d6d6d6"),
                textSelectionBg: fade(accent, 0.35),
                scrollTrack: hex("#f0f0f0"),
                scrollThumb: hex("#a6a6a6"),
                scrollThumbHover: hex("#767676"),
                scrollThumbPressed: hex("#000000"),
                headerBg: hex("#ffffff"),
                headerBgHover: hex("#f2f2f2"),
                headerText: hex("#6b6b6b"),
                gridLine: hex("#e0e0e0"),
                focusRing: hex("#000000"),
                shadow: withAlpha(hex("#000000"), 0),
              },
            },
      ),
    });
    this.variant = variant;
  }

  // --- window chrome ----------------------------------------------------

  /**
   * A rule under the caption on the active window, in the accent.
   *
   * The frame is accent-colored already, so this is not what identifies the
   * window; it is what separates a chromeless caption from the content below
   * it, which is the same color and would otherwise run together.
   */
  protected override decorateWindow(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    if (!s.active) return;
    const b = this.metrics.windowBorder;
    rc.fillRect(
      { x: b, y: b + this.captionHeight(s) - 2, width: bounds.width - b * 2, height: 2 },
      this.palette.accent,
    );
  }

  protected override captionButtonHighlight(button: CaptionButton, pressed: boolean): Color {
    const p = this.palette;
    if (button === "close") return pressed ? CLOSE_RED_PRESSED : CLOSE_RED;
    return pressed ? mixColor(p.controlBgHover, p.windowBg, 0.35) : p.controlBgHover;
  }

  protected override closeGlyphInk(): Color {
    return hex("#ffffff");
  }

  // --- controls ---------------------------------------------------------

  /**
   * Flat fill, no border at rest, and a hard 2px outline under the pointer.
   *
   * `buttonTextColor` is inherited unchanged, so every fill here has to stay
   * legible under `palette.text` — see the note at the top of the file.
   */
  override drawButton(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const accent = s.isDefault === true;

    let fill: Color;
    if (s.disabled) fill = accent ? fade(p.accent, 0.35) : p.controlBgDisabled;
    else if (s.pressed) fill = accent ? p.accentPressed : p.controlBgPressed;
    else if (accent) fill = s.hovered ? p.accentHover : p.accent;
    else fill = s.hovered ? p.controlBgHover : p.controlBg;

    rc.fillRect(r, fill);
    // The outline *is* the hover state; nothing else in the theme draws one.
    if (s.hovered && !s.disabled) rc.strokeRect(r, p.text, 2, 0);
    if (s.focused && !s.disabled) this.drawFocusRing(rc, r);
  }

  /** A 2px box, recolored rather than thickened on hover, accent on focus. */
  override drawTextBox(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.disabled ? p.fieldBgDisabled : p.fieldBg,
      borderWidth: 2,
      borderColor: s.disabled
        ? p.controlBorder
        : s.focused
          ? p.fieldBorderFocused
          : s.hovered
            ? p.controlBorderHover
            : p.fieldBorder,
    });
  }

  /** A square 2px outline; checked fills solid with the accent. */
  override drawCheckBox(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const on = s.checked === true || s.indeterminate === true;

    let fill: Color;
    if (s.disabled) fill = on ? fade(p.accent, 0.35) : p.fieldBgDisabled;
    else if (on) fill = s.pressed ? p.accentPressed : s.hovered ? p.accentHover : p.accent;
    else fill = p.fieldBg;

    const border = s.disabled
      ? p.controlBorder
      : s.pressed || s.hovered
        ? p.controlBorderHover
        : p.fieldBorder;
    rc.drawRect(r, { fill, borderWidth: 2, borderColor: on ? fill : border });

    const ink = s.disabled ? p.textDisabled : p.accentText;
    if (s.indeterminate) {
      rc.fillRect({ x: r.x + 5, y: r.y + r.height / 2 - 1, width: r.width - 10, height: 2 }, ink);
    } else if (s.checked) {
      drawCheckmark(rc, r, ink);
    }
  }

  /** The one round thing in the theme: a 2px ring with a solid center. */
  override drawRadioButton(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const radius = Math.min(r.width, r.height) / 2;
    const on = s.checked === true;
    const border = s.disabled
      ? p.controlBorder
      : on
        ? s.pressed
          ? p.accentPressed
          : s.hovered
            ? p.accentHover
            : p.accent
        : s.pressed || s.hovered
          ? p.controlBorderHover
          : p.fieldBorder;

    rc.drawRect(r, {
      fill: s.disabled ? p.fieldBgDisabled : p.fieldBg,
      radius,
      borderWidth: 2,
      borderColor: border,
    });
    if (!on) return;
    const dot = Math.max(4, Math.round(r.width * 0.45));
    const inset = (r.width - dot) / 2;
    rc.fillRect(
      { x: r.x + inset, y: r.y + inset, width: dot, height: dot },
      s.disabled ? p.textDisabled : p.accent,
      dot / 2,
    );
  }

  /**
   * A pivot rather than a folder: words in a row, an accent bar under the one
   * showing, and a box on none of them.
   *
   * The bar stops a pixel short of the page, because `TabControl` repaints the
   * shared edge in `windowBg` afterwards and would otherwise eat it.
   */
  override drawTab(rc: RenderContext, r: Rect, s: TabVisualState): void {
    const p = this.palette;
    if (s.hovered && !s.selected && !s.disabled) rc.fillRect(r, p.controlBgHover);
    if (!s.selected) return;

    const thickness = 3;
    const y = s.bottom ? r.y + 1 : r.y + r.height - 1 - thickness;
    rc.fillRect(
      { x: r.x, y, width: r.width, height: thickness },
      s.disabled ? p.textDisabled : p.accent,
    );
  }

  /** Live tab in full ink, the rest grayed back: the pivot's whole hierarchy. */
  override tabTextColor(s: TabVisualState): Color {
    const p = this.palette;
    if (s.disabled) return p.textDisabled;
    if (s.selected) return p.text;
    return s.hovered ? p.text : p.textSubtle;
  }

  /** A flat block. Sunken versus raised is not a distinction Metro makes. */
  override drawPanel(rc: RenderContext, r: Rect, sunken: boolean): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: sunken ? p.fieldBg : p.windowBg,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });
  }

  /**
   * A thin rule at rest that becomes a square bar on a solid track once the
   * pointer arrives — Win8's slim indicator, minus the stepper arrows it grew.
   */
  override drawScrollBar(rc: RenderContext, parts: ScrollBarParts, s: VisualState): void {
    const p = this.palette;
    const hovered = parts.hoveredPart === "thumb" || s.hovered;
    const pressed = parts.pressedPart === "thumb";
    const wide = hovered || pressed;

    if (wide) rc.fillRect(parts.bounds, p.scrollTrack);
    if (parts.thumb.width <= 0 || parts.thumb.height <= 0) return;

    const color = pressed ? p.scrollThumbPressed : hovered ? p.scrollThumbHover : p.scrollThumb;
    const t = parts.thumb;
    const thick = wide ? (parts.vertical ? t.width : t.height) : 3;
    const r: Rect = parts.vertical
      ? { x: t.x + (t.width - thick) / 2, y: t.y, width: thick, height: t.height }
      : { x: t.x, y: t.y + (t.height - thick) / 2, width: t.width, height: thick };
    rc.fillRect(r, color);
  }

  /** Typographic: no fill of its own, one rule below, accent under the pointer. */
  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const live = s.hovered || s.pressed;
    rc.fillRect(r, live ? p.headerBgHover : p.headerBg);
    const thickness = live ? 2 : 1;
    rc.fillRect(
      { x: r.x, y: r.y + r.height - thickness, width: r.width, height: thickness },
      live ? p.accent : p.gridLine,
    );
  }

  /**
   * Five dots crossing the track, bunching at both ends.
   *
   * Metro's indeterminate bar is traveling dots, not a sliding block, and it
   * is the one animation the language gets recognized by. The determinate case
   * is a flat accent block on a flat track — no border, no radius, no chunks.
   */
  override drawProgressBar(rc: RenderContext, r: Rect, v: ProgressVisual): void {
    const p = this.palette;
    rc.fillRect(r, this.isDark ? p.controlBgDisabled : p.controlBg);
    if (r.width <= 0 || r.height <= 0) return;
    const ink = v.disabled ? p.textDisabled : p.accent;

    if (!v.indeterminate) {
      const w = Math.round(r.width * Math.min(1, Math.max(0, v.fraction)));
      if (w > 0) rc.fillRect({ ...r, width: w }, ink);
      return;
    }

    const count = 5;
    const dot = Math.max(3, Math.min(6, Math.round(r.height * 0.45)));
    const y = r.y + (r.height - dot) / 2;
    const travel = r.width + dot * 2;
    for (let i = 0; i < count; i++) {
      // A stagger short enough that the five still read as one moving group.
      const t = (((v.phase - i * 0.055) % 1) + 1) % 1;
      const x = r.x - dot + easeInOut(t) * travel;
      if (x + dot < r.x || x > r.x + r.width) continue;
      rc.fillRect({ x, y, width: dot, height: dot }, ink, dot / 2);
    }
  }

  /**
   * The same five dots, sent round a circle instead of across a track.
   *
   * They chase one another rather than sitting at fixed points, so the ring
   * reads as turning even though this renderer has no rotation to give it.
   */
  override drawSpinner(rc: RenderContext, r: Rect, s: SpinnerVisual): void {
    const p = this.palette;
    const size = Math.min(r.width, r.height);
    if (size <= 0) return;
    const count = 5;
    const dot = Math.max(2, Math.round(size * 0.16));
    const radius = (size - dot) / 2;
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;
    const ink = s.disabled ? p.textDisabled : p.accent;

    for (let i = 0; i < count; i++) {
      const t = (((s.phase - i * 0.05) % 1) + 1) % 1;
      // Eased around the turn, so the group bunches and stretches each lap.
      const angle = easeInOut(t) * Math.PI * 2 - Math.PI / 2;
      rc.fillRect(
        {
          x: cx + Math.cos(angle) * radius - dot / 2,
          y: cy + Math.sin(angle) * radius - dot / 2,
          width: dot,
          height: dot,
        },
        ink,
        dot / 2,
      );
    }
  }

  /** Square dots under a carousel: nothing else in this theme is a pill. */
  override drawCarouselDot(rc: RenderContext, r: Rect, active: boolean, hovered: boolean): void {
    const p = this.palette;
    const size = Math.min(r.width, r.height);
    const box: Rect = {
      x: r.x + (r.width - size) / 2,
      y: r.y + (r.height - size) / 2,
      width: size,
      height: size,
    };
    rc.fillRect(box, active ? p.accent : hovered ? p.textSubtle : p.controlBorder);
  }

  /** A fine dotted rectangle, the way WinRT marked keyboard focus. */
  override drawFocusRing(rc: RenderContext, r: Rect): void {
    const i = this.metrics.focusRingInset;
    const x0 = Math.round(r.x + i);
    const y0 = Math.round(r.y + i);
    const x1 = Math.round(r.x + r.width - i);
    const y1 = Math.round(r.y + r.height - i);
    const ink = this.palette.focusRing;
    if (x1 <= x0 || y1 <= y0) return;
    rc.fillChecker({ x: x0, y: y0, width: x1 - x0, height: 1 }, ink);
    rc.fillChecker({ x: x0, y: y1 - 1, width: x1 - x0, height: 1 }, ink);
    rc.fillChecker({ x: x0, y: y0, width: 1, height: y1 - y0 }, ink);
    rc.fillChecker({ x: x1 - 1, y: y0, width: 1, height: y1 - y0 }, ink);
  }
}
